import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api, notifySocialHandleApprovalPending } from '../api';
import { logHttpError } from '../utils/httpError';
import { Paper, Typography, Box, CircularProgress, Button, Card, CardContent, CardMedia, Alert, Grid, Chip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PageHeader from './common/PageHeader';
import LoadingBlock from './common/LoadingBlock';
import { Link } from 'react-router-dom';
import { getSocialLink, platformLabel, platformColor } from '../utils/socialLinks';

export default function ApproveSocialHandles() {
  const theme = useTheme();
  const [users, setUsers] = useState([]);
  const [currentUserIndex, setCurrentUserIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(new Set());
  const [results, setResults] = useState({});
  const processingRef = useRef(new Set());

  const fetchPendingHandles = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/admin/pending-social-media-handles');
      const data = res.data?.data || [];
      setUsers(data);
      setCurrentUserIndex(0);
      setProcessing(new Set());
      processingRef.current = new Set();
      setResults({});
      notifySocialHandleApprovalPending(data.length > 0);
    } catch (err) {
      logHttpError(err, 'Fetch pending social handles');
      setError('Failed to fetch pending social handles. Please try refreshing.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingHandles();
  }, [fetchPendingHandles]);

  const currentUser = users[currentUserIndex];
  const currentHandles = currentUser?.pending_handles || [];
  const remainingHandles = currentHandles.filter(h => !results[h.id]);

  // Auto-advance when all handles for current user are actioned
  useEffect(() => {
    if (!currentUser) return;
    if (currentHandles.length > 0 && remainingHandles.length === 0) {
      const timer = setTimeout(() => {
        goNextUser();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [remainingHandles.length, currentUser]);

  const goNextUser = useCallback(() => {
    setResults({});
    setCurrentUserIndex(prev => prev + 1);
  }, []);

  const handleApprove = useCallback(async (handleId) => {
    if (processingRef.current.has(handleId)) return;
    processingRef.current = new Set([...processingRef.current, handleId]);
    setProcessing(new Set(processingRef.current));
    try {
      await api.post('/admin/approve-social-media-handle', { handle_id: handleId });
      setResults(prev => ({ ...prev, [handleId]: { success: true, action: 'approved' } }));
    } catch (err) {
      const details = logHttpError(err, 'Approve Social Handle');
      const msg = details?.data?.message || err?.message || 'Failed to approve handle';
      setResults(prev => ({ ...prev, [handleId]: { success: false, message: msg } }));
    } finally {
      processingRef.current = new Set([...processingRef.current].filter(id => id !== handleId));
      setProcessing(new Set(processingRef.current));
    }
  }, []);

  const handleReject = useCallback(async (handleId) => {
    if (processingRef.current.has(handleId)) return;
    processingRef.current = new Set([...processingRef.current, handleId]);
    setProcessing(new Set(processingRef.current));
    try {
      await api.post('/admin/reject-social-media-handle', { handle_id: handleId });
      setResults(prev => ({ ...prev, [handleId]: { success: true, action: 'rejected' } }));
    } catch (err) {
      const details = logHttpError(err, 'Reject Social Handle');
      const msg = details?.data?.message || err?.message || 'Failed to reject handle';
      setResults(prev => ({ ...prev, [handleId]: { success: false, message: msg } }));
    } finally {
      processingRef.current = new Set([...processingRef.current].filter(id => id !== handleId));
      setProcessing(new Set(processingRef.current));
    }
  }, []);

  const handleApproveAll = useCallback(async () => {
    if (!currentUser) return;
    const toApprove = currentHandles.filter(h => !results[h.id]);
    for (const h of toApprove) {
      await handleApprove(h.id);
    }
  }, [currentUser, currentHandles, results, handleApprove]);

  // Keyboard shortcuts: A = approve all, N = next user
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      const key = e.key.toLowerCase();
      if (key === 'a') handleApproveAll();
      if (key === 'n') goNextUser();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleApproveAll, goNextUser]);

  if (loading) return <LoadingBlock height={420} />;

  if (error) {
    return (
      <>
        <PageHeader title="Social Handle Approval" />
        <Paper sx={{ p: 4 }}>
          <Alert severity="error">{error}</Alert>
        </Paper>
      </>
    );
  }

  if (users.length === 0 || currentUserIndex >= users.length) {
    return (
      <>
        <PageHeader title="Social Handle Approval" />
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h5">All social handles have been reviewed!</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={fetchPendingHandles}>Refresh</Button>
        </Paper>
      </>
    );
  }

  const verificationVideo = currentUser.humanVerificationVideos?.[0];
  const userImages = currentUser.images || [];

  return (
    <>
      <PageHeader title="Social Handle Approval" />
      <Paper sx={{ p: 2, maxWidth: 1400, mx: 'auto' }}>
        {/* User info bar */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <Typography variant="h6">
            User:{' '}
            <Link
              to={`/user/${currentUser.user_id}`}
              state={{ from: { label: 'Approve Social Handles', path: '/admin/social-handle-approval' } }}
              style={{ textDecoration: 'none', color: theme.palette.primary.main, fontWeight: 700 }}
            >
              {currentUser.username || `User #${currentUser.user_id}`}
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">ID: {currentUser.user_id}</Typography>
          {currentUser.is_creator ? (
            <Chip label={currentUser.is_approved_creator ? 'Approved Creator' : 'Creator'} color={currentUser.is_approved_creator ? 'success' : 'primary'} size="small" />
          ) : (
            <Chip label="Fan" color="secondary" size="small" />
          )}
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
            User {currentUserIndex + 1} of {users.length}
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Left: Pending handles */}
          <Grid item xs={12} md={8}>
            <Grid container spacing={2}>
              {currentHandles.map((h) => {
                const result = results[h.id];
                const isProcessing = processing.has(h.id);
                const { url, problem } = getSocialLink(h.type, h.handle);

                return (
                  <Grid item xs={12} sm={6} key={h.id}>
                    <Card sx={{
                      opacity: result?.success ? 0.5 : 1,
                      transition: 'opacity 0.3s',
                    }}>
                      <CardContent>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Chip
                            label={platformLabel(h.type)}
                            size="small"
                            sx={{
                              bgcolor: platformColor(h.type),
                              color: '#fff',
                              fontWeight: 600,
                            }}
                          />
                          <Typography variant="caption" color="text.secondary">ID: {h.id}</Typography>
                        </Box>
                        {url ? (
                          <Typography
                            component="a"
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ fontSize: '1.1rem', fontWeight: 600, color: theme.palette.primary.main, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                          >
                            {h.handle}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontSize: '1.1rem', fontWeight: 600 }}>{h.handle}</Typography>
                        )}
                        {problem && !result && (
                          <Alert severity="warning" sx={{ mt: 1, py: 0 }}>
                            {problem}
                          </Alert>
                        )}
                        {result ? (
                          <Alert severity={result.success ? 'success' : 'error'} sx={{ mt: 1, py: 0 }}>
                            {result.success ? (result.action === 'approved' ? 'Approved' : 'Rejected') : result.message}
                          </Alert>
                        ) : (
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Button
                              size="small"
                              variant="contained"
                              color="success"
                              fullWidth
                              onClick={() => handleApprove(h.id)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? <CircularProgress size={18} /> : 'Approve'}
                            </Button>
                            <Button
                              size="small"
                              variant="contained"
                              color="error"
                              fullWidth
                              onClick={() => handleReject(h.id)}
                              disabled={isProcessing}
                            >
                              {isProcessing ? <CircularProgress size={18} /> : 'Reject'}
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Grid>

          {/* Right: Context — profile images + verification video */}
          <Grid item xs={12} md={4}>
            {/* Profile images */}
            {userImages.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>Profile Images</Typography>
                <Grid container spacing={1}>
                  {userImages.map((img, i) => (
                    <Grid item xs={4} key={img.id || i}>
                      {img.secureUrl ? (
                        <Box
                          component="img"
                          src={img.secureUrl}
                          alt={`Profile ${i + 1}`}
                          sx={{ width: '100%', aspectRatio: '1', objectFit: 'cover', borderRadius: 1, bgcolor: '#f0f0f0' }}
                        />
                      ) : (
                        <Box sx={{ width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">N/A</Typography>
                        </Box>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {/* Verification video */}
            {verificationVideo && (
              <Card>
                <Typography variant="subtitle2" sx={{ p: 1, bgcolor: '#f5f5f5', textAlign: 'center', fontWeight: 600 }}>
                  Verification Video
                </Typography>
                {verificationVideo.secureUrl ? (
                  <Box
                    component="video"
                    src={verificationVideo.secureUrl}
                    autoPlay
                    loop
                    muted
                    sx={{ width: '100%', maxHeight: 400, objectFit: 'contain', bgcolor: '#000' }}
                  />
                ) : (
                  <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f0f0f0' }}>
                    <Typography color="text.secondary">Video not available</Typography>
                  </Box>
                )}
              </Card>
            )}
          </Grid>
        </Grid>

        {/* Bottom actions */}
        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
          <Button
            size="large"
            variant="contained"
            color="success"
            onClick={handleApproveAll}
            disabled={remainingHandles.length === 0}
          >
            Approve All ({remainingHandles.length})
          </Button>
          <Button
            size="large"
            variant="outlined"
            onClick={goNextUser}
          >
            Next User
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
          Keyboard: A = Approve All, N = Next User
        </Typography>
      </Paper>
    </>
  );
}
